import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Copy, Instagram, Quote, Star, Upload, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { http, img, errMsg, useGet } from "@/lib/api";
import { useShop, useSeo } from "@/lib/store";
import { SectionHead } from "@/components/Brand";
import { ProductRail } from "@/components/ProductCard";
import { ReelCard } from "@/components/ReelCard";

const W = ({ children, className = "" }) => <section className={`max-w-[1500px] mx-auto px-4 sm:px-8 ${className}`}>{children}</section>;

function Hero() {
  const { settings: s } = useShop();
  const head = (s.hero_headline || "NOT MADE TO FIT IN.").split(" ");
  return (
    <section className="relative min-h-[calc(100vh-100px)] overflow-hidden grain" data-testid="hero-section">
      {s.hero_image && <img src={img(s.hero_image)} alt="DEN & THREADS streetwear campaign" className="absolute inset-0 w-full h-full object-cover scale-105" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-transparent" />
      <div className="relative max-w-[1500px] mx-auto px-4 sm:px-8 min-h-[calc(100vh-100px)] flex flex-col justify-end pb-16 sm:pb-24">
        <p className="eyebrow reveal">SS/26 DROP · KANPUR → WORLD</p>
        <h1 className="font-display leading-[0.86] mt-5 text-[19vw] sm:text-[12vw] lg:text-[9.5rem] max-w-5xl">
          {head.map((w, i) => <span key={i}>{i === Math.ceil(head.length / 2) && <br />}<span className={`inline-block reveal mr-[0.18em] ${i >= head.length - 2 ? "text-neon" : ""}`} style={{ animationDelay: `${120 + i * 90}ms` }}>{w}</span></span>)}
        </h1>
        <p className="text-base sm:text-lg text-white/80 max-w-md mt-6 reveal" style={{ animationDelay: "500ms" }}>{s.hero_sub}</p>
        <div className="flex flex-wrap gap-3 mt-9 reveal" style={{ animationDelay: "650ms" }}>
          <Link to="/shop" className="btn-neon" data-testid="hero-shop-button">SHOP THE DROP <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/custom-printing" className="btn-ghost" data-testid="hero-customize-button">CUSTOMIZE YOUR FIT</Link>
        </div>
        <div className="hidden lg:block absolute right-8 bottom-24 w-64 border border-white/15 bg-black/40 backdrop-blur-xl p-4 reveal" style={{ animationDelay: "800ms" }}>
          {s.hero_image_2 && <img src={img(s.hero_image_2)} alt="" className="aspect-[4/5] w-full object-cover" />}
          <p className="eyebrow text-[10px] mt-3">NOW DROPPING</p>
          <p className="text-sm font-semibold">Anime After Dark — Vol.01</p>
        </div>
      </div>
    </section>
  );
}

function Offers() {
  const { data = [] } = useGet("/offers");
  const copy = (c) => {
    Promise.resolve(navigator.clipboard?.writeText(c)).then(() => toast.success(`Code ${c} copied`)).catch(() => toast(`Use code ${c} at checkout`));
  };
  return (
    <W className="py-16">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 border border-white/10" data-testid="offers-section">
        {data.map((o, i) => (
          <div key={o.id} className={`p-7 relative overflow-hidden ${i ? "border-t sm:border-t-0 sm:border-l border-white/10" : ""} ${o.code ? "bg-neon text-black" : ""}`} data-testid={`offer-card-${i}`}>
            <p className={`font-mono text-[10px] tracking-[.25em] ${o.code ? "" : "text-neon"}`}>OFFER 0{i + 1} · LIMITED TIME</p>
            <p className="font-display text-3xl mt-3 leading-none">{o.title}</p>
            <p className={`text-sm mt-3 ${o.code ? "text-black/70" : "text-white/60"}`}>{o.description}</p>
            {o.code && <button onClick={() => copy(o.code)} data-testid={`copy-coupon-${o.code}`} className="mt-5 inline-flex items-center gap-2 border-2 border-dashed border-black px-4 py-2 font-mono font-bold">{o.code} <Copy className="w-4 h-4" /></button>}
            {!o.code && o.kind === "banner" && <Link to={i === 2 ? "/wholesale" : "/anime-after-dark"} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold hover:text-neon">Explore <ArrowUpRight className="w-4 h-4" /></Link>}
          </div>
        ))}
      </div>
    </W>
  );
}

function Categories() {
  const { data = [] } = useGet("/categories");
  return (
    <W className="py-12">
      <SectionHead eyebrow="Shop by category" title={<>FIND YOUR <span className="stroke-text">UNIFORM</span></>} action={<Link to="/shop" className="hidden sm:flex btn-ghost">All products</Link>} />
      <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x pb-2" data-testid="category-rail">
        {data.map((c, i) => (
          <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`category-${c.slug}`} className="group relative shrink-0 snap-start w-[62vw] sm:w-[260px] aspect-[3/4] overflow-hidden bg-[#171717]">
            <img src={img(c.image)} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-[opacity,transform] duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />
            <span className="absolute top-4 left-4 font-mono text-[11px] text-neon">{String(i + 1).padStart(2, "0")}</span>
            <p className="absolute bottom-4 left-4 right-4 font-display text-2xl leading-none group-hover:text-neon transition-colors">{c.name}</p>
          </Link>
        ))}
      </div>
    </W>
  );
}

function Rail({ eyebrow, title, query, to }) {
  return (
    <W className="py-14">
      <SectionHead eyebrow={eyebrow} title={title} action={<Link to={to} className="text-sm font-semibold uppercase tracking-widest hover:text-neon flex items-center gap-1 shrink-0">View all <ArrowRight className="w-4 h-4" /></Link>} />
      <ProductRail query={query} />
    </W>
  );
}

function AnimeTeaser() {
  const { data = [] } = useGet("/anime");
  return (
    <section className="relative py-24 my-10 bg-[#050505] overflow-hidden" data-testid="anime-teaser">
      <div className="absolute -top-10 left-0 font-display text-[22vw] leading-none text-white/[0.03] whitespace-nowrap select-none">AFTER DARK</div>
      <W>
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 items-end">
          <div>
            <p className="eyebrow text-red-500">Exclusive collection · Vol.01</p>
            <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.85] mt-4">ANIME<br /><span className="text-red-600">AFTER</span><br />DARK</h2>
            <p className="text-white/60 mt-6 max-w-sm">Four original manga-born artworks. Small chest logo on front, massive print on the back. Limited numbered runs.</p>
            <Link to="/anime-after-dark" className="btn-neon mt-8" data-testid="anime-teaser-cta">Enter the collection <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.map((a, i) => (
              <Link key={a.slug} to={`/anime-after-dark#${a.slug}`} className={`group relative aspect-[2/3] overflow-hidden ${i % 2 ? "sm:translate-y-10" : ""}`} data-testid={`anime-teaser-${a.slug}`}>
                <img src={img(a.art)} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-80" />
                <p className="absolute bottom-3 left-3 font-display text-xl leading-none">{a.code} · {a.title}</p>
              </Link>
            ))}
          </div>
        </div>
      </W>
    </section>
  );
}

function StudioPromo() {
  return (
    <W className="py-16">
      <div className="grid lg:grid-cols-2 gap-4">
        <Link to="/custom-printing" className="group relative bg-neon text-black p-10 sm:p-14 min-h-[420px] flex flex-col justify-between overflow-hidden" data-testid="studio-promo">
          <Sparkles className="w-10 h-10" />
          <div>
            <p className="font-mono text-xs tracking-[.25em]">CUSTOM PRINTING STUDIO</p>
            <h3 className="font-display text-5xl sm:text-6xl leading-[0.9] mt-3">YOUR DESIGN.<br />OUR PRINT.<br />YOUR IDENTITY.</h3>
            <p className="mt-4 max-w-sm">Tees, oversized tees, hoodies & sweatshirts. Front, back, chest, sleeve — live preview before you order.</p>
          </div>
          <ArrowUpRight className="absolute top-10 right-10 w-14 h-14 group-hover:rotate-45 transition-transform duration-300" />
        </Link>
        <Link to="/upload-design" className="group relative border border-white/15 p-10 sm:p-14 min-h-[420px] flex flex-col justify-between overflow-hidden hover:border-neon transition-colors" data-testid="upload-promo">
          <Upload className="w-10 h-10 text-neon" />
          <div>
            <p className="eyebrow">Upload your own design</p>
            <h3 className="font-display text-5xl sm:text-6xl leading-[0.9] mt-3">GOT ART?<br />DROP IT HERE.</h3>
            <p className="mt-4 max-w-sm text-white/60">PNG, JPG, WEBP or PDF. Personal orders, bulk printing, business merch — we quote within 24 hours.</p>
          </div>
          <span className="btn-ghost self-start mt-6 group-hover:bg-white group-hover:text-black">Upload artwork</span>
        </Link>
      </div>
    </W>
  );
}

function ReelsStrip() {
  const { data = [] } = useGet("/reels");
  if (!data.length) return null;
  return (
    <W className="py-16">
      <SectionHead eyebrow="The Den Experience" title="WATCH THE DROP" action={<Link to="/reels" className="text-sm font-semibold uppercase tracking-widest hover:text-neon flex items-center gap-1 shrink-0">All reels <ArrowRight className="w-4 h-4" /></Link>} />
      <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x" data-testid="home-reels">
        {data.slice(0, 6).map((r) => <div key={r.id} className="shrink-0 w-[60vw] sm:w-[260px] snap-start"><ReelCard r={r} /></div>)}
      </div>
    </W>
  );
}

function WholesalePromo() {
  const { settings: s } = useShop();
  return (
    <W className="py-16">
      <div className="relative border border-white/10 p-8 sm:p-14 grid lg:grid-cols-[1.3fr_1fr] gap-10 overflow-hidden grain" data-testid="wholesale-promo">
        <div>
          <p className="eyebrow">B2B · Wholesale · Private label</p>
          <h3 className="font-display text-5xl sm:text-7xl leading-[0.88] mt-4">YOUR BRAND.<br /><span className="text-neon">OUR PRODUCTION.</span></h3>
          <p className="text-white/60 mt-5 max-w-lg">{s.promo_text || "Bulk printing for brands, resellers, events and corporates."}</p>
          <Link to="/wholesale" className="btn-neon mt-8" data-testid="wholesale-promo-cta">GET WHOLESALE QUOTE <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-2 gap-px bg-white/10 self-center">
          {[["50+", "MOQ pieces"], ["7-12", "Day turnaround"], ["240-400", "GSM fabrics"], ["PAN", "India shipping"]].map(([a, b]) => (
            <div key={b} className="bg-[#080808] p-6"><p className="font-display text-4xl text-neon">{a}</p><p className="font-mono text-[11px] text-grey mt-1 uppercase tracking-widest">{b}</p></div>
          ))}
        </div>
      </div>
    </W>
  );
}

const REVIEWS = [
  ["Aarav S.", "Delhi", "The Shadow Samurai back print is insane in person. Heavy fabric, zero fade after 10 washes."],
  ["Riya M.", "Lucknow", "Uploaded my own art for our college fest — 120 hoodies delivered on time. Print quality was crisp."],
  ["Kabir J.", "Mumbai", "Finally an Indian streetwear label that gets oversized fits right. Baggy jeans + Core hoodie = daily uniform."],
];

function Testimonials() {
  return (
    <W className="py-16">
      <SectionHead eyebrow="Real people. Real fits." title="FROM THE DEN" />
      <div className="grid md:grid-cols-3 gap-4">
        {REVIEWS.map(([n, c, t], i) => (
          <figure key={n} className="bg-[#171717] p-8 border border-transparent hover:border-neon transition-colors reveal" style={{ animationDelay: `${i * 100}ms` }} data-testid={`testimonial-${i}`}>
            <Quote className="w-8 h-8 text-neon" />
            <blockquote className="mt-5 text-lg leading-relaxed">{t}</blockquote>
            <div className="flex gap-0.5 mt-6">{[0, 1, 2, 3, 4].map((x) => <Star key={x} className="w-4 h-4 fill-current text-neon" />)}</div>
            <figcaption className="mt-3 font-mono text-xs text-grey">{n.toUpperCase()} — {c.toUpperCase()}</figcaption>
          </figure>
        ))}
      </div>
    </W>
  );
}

function Gallery() {
  const { settings: s } = useShop();
  return (
    <W className="py-16">
      <SectionHead eyebrow="@denandthreads" title="TAG US. GET FEATURED." action={s.instagram && <a href={s.instagram} target="_blank" rel="noreferrer" className="btn-ghost hidden sm:flex" data-testid="instagram-follow-link"><Instagram className="w-4 h-4" /> Follow</a>} />
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2" data-testid="instagram-gallery">
        {(s.gallery || []).map((g, i) => (
          <a key={i} href={s.instagram || "#"} target="_blank" rel="noreferrer" className="group relative aspect-square overflow-hidden">
            <img src={img(g)} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
            <div className="absolute inset-0 bg-neon/0 group-hover:bg-neon/70 grid place-items-center transition-colors"><Instagram className="w-7 h-7 text-black opacity-0 group-hover:opacity-100" /></div>
          </a>
        ))}
      </div>
    </W>
  );
}

function Newsletter() {
  const [email, setEmail] = useState("");
  const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await http.post("/newsletter", { email }); setOk(true); toast.success("You're on the list."); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <W className="py-16">
      <div className="bg-[#171717] p-8 sm:p-14 grid lg:grid-cols-2 gap-8 items-center" data-testid="newsletter-section">
        <div>
          <p className="eyebrow">Join the den</p>
          <h3 className="font-display text-4xl sm:text-6xl leading-[0.9] mt-3">EARLY DROPS.<br />SECRET CODES.</h3>
        </div>
        {ok ? <p className="font-display text-3xl text-neon" data-testid="newsletter-success">YOU'RE IN. WATCH YOUR INBOX.</p> : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="field flex-1" data-testid="newsletter-email-input" />
            <button className="btn-neon" data-testid="newsletter-submit-button">Subscribe</button>
          </form>
        )}
      </div>
    </W>
  );
}

export default function Home() {
  useSeo(null);
  return (
    <div data-testid="home-page">
      <Hero />
      <Offers />
      <Categories />
      <Rail eyebrow="Trending now" title="WHAT THE DEN IS WEARING" query="tag=trending" to="/shop?tag=trending" />
      <Rail eyebrow="Just landed" title="NEW ARRIVALS" query="tag=new&sort=new" to="/shop?tag=new" />
      <AnimeTeaser />
      <Rail eyebrow="Proven heat" title="BEST SELLERS" query="tag=bestseller&sort=popular" to="/shop?tag=bestseller" />
      <StudioPromo />
      <ReelsStrip />
      <WholesalePromo />
      <Testimonials />
      <Gallery />
      <Newsletter />
    </div>
  );
}
